
import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DateMaskDirective } from "../directives/date-mask.directive";
import { TimeMaskDirective } from "../directives/time-mask.directive";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";

// ✅ PrimeNG 15 Standalone Components (No Modules)
import { AccordionModule } from "primeng/accordion";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from "primeng/checkbox";
import { Select } from "primeng/select";
import { PasswordModule } from "primeng/password";
import { MenuModule } from "primeng/menu";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { TieredMenuModule } from "primeng/tieredmenu";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { RadioButton } from "primeng/radiobutton";
import { MessagesModule } from "primeng/messages";
import { MessageModule } from "primeng/message";
import { DatePicker } from "primeng/datepicker";
import { CalendarModule } from "primeng/calendar";
import { InputTextModule } from "primeng/inputtext";
import { InputMaskModule } from "primeng/inputmask";
import { Fluid } from "primeng/fluid";
import {ChipsModule} from "primeng/chips";
import { TabsModule } from "primeng/tabs";
import { EditorModule } from "primeng/editor";
import { FileUploadModule } from "primeng/fileupload";
import { ProgressBarModule } from "primeng/progressbar";
import { PaginatorModule } from "primeng/paginator";
import { SplitButtonModule } from "primeng/splitbutton";
import { AutoCompleteModule } from "primeng/autocomplete";
import { MultiSelectModule } from "primeng/multiselect";
import { ToastModule } from "primeng/toast";
import { ChartModule } from "primeng/chart";
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { DrawerModule } from 'primeng/drawer'
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import {
  GoogleMap,
  MapMarker,
  MapAdvancedMarker,
  MapInfoWindow,
  MapMarkerClusterer,
} from "@angular/google-maps";
import { FullCalendarModule } from "@fullcalendar/angular";
// ✅ Other Third-Party Modules
import { DialogModule } from "primeng/dialog";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { NgxSpinnerModule } from "ngx-spinner";
import { NgxPaginationModule } from "ngx-pagination";
import { NgxPrintModule } from "ngx-print";
import { TranslateModule } from "@ngx-translate/core";

import { FooterComponent } from "./footer/footer.component";
import { DashboardHeaderComponent } from "./headers/dashboard-header/dashboard-header.component";
import { AiUsageComponent } from "./headers/ai-usage/ai-usage.component";
import { NoPermissionComponent } from "./error/no-permission/no-permission.component";
import { ReloginComponent } from "./error/relogin/relogin.component";
import { DeleteComponent } from "./error/delete/delete.component";
import { ClientsubnavComponent } from "./clientsubnav/clientsubnav.component";
import { SystemMessageComponent } from "./system-message/system-message.component";
import { PayrollBillingComponent } from "./payroll-billing/payroll-billing.component";

// ✅ Services & Interceptors
import { SharedService } from "./shared.service";
import { InterseptorsService } from "../interseptors.service";

import { environment } from '../../environments/environment';
import { TooltipModule } from "primeng/tooltip";
import { Tooltip } from "chart.js";


/*Overriding isValidDate because it's not evaluating any dates from
p-datepicker as dates and the fields are empty, 
despite the value being correctly updated*/
DatePicker.prototype.isValidDate = function (date) {
  return date instanceof Date && !isNaN(date.getTime());
};

@NgModule({
  declarations: [
    FooterComponent,
    DashboardHeaderComponent,
    AiUsageComponent,
    NoPermissionComponent,
    ReloginComponent,
    DeleteComponent,
    ClientsubnavComponent,
    SystemMessageComponent,
    DateMaskDirective,
    TimeMaskDirective,
    PayrollBillingComponent,
  ],
  exports: [
    CommonModule,
    DateMaskDirective,
    TimeMaskDirective,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AngularEditorModule,
    DragDropModule,
    NgxSpinnerModule,
    NgxPaginationModule,
    NgxPrintModule,
    TranslateModule,
    // ✅ Export PrimeNG 15 Components
    AccordionModule,
    ToggleSwitchModule,
    TextareaModule,
    CheckboxModule,
    Select,
    PasswordModule,
    MenuModule,
    TableModule,
    ProgressBarModule,
    ButtonModule,
    TieredMenuModule,
    BreadcrumbModule,
    RadioButton,
    MessagesModule,
    MessageModule,
    DatePicker,
    InputTextModule,
    InputMaskModule,
    OverlayPanelModule,
    Fluid,
    TabsModule,
    EditorModule,
    FileUploadModule,
    PaginatorModule,
    SplitButtonModule,
    AutoCompleteModule,
    MultiSelectModule,
    ChipsModule,
    ChartModule,
    DrawerModule,
    TagModule,
    ToastModule,
    GoogleMap,
    MapMarker,
    MapAdvancedMarker,
    TooltipModule,
    MapInfoWindow,
    MapMarkerClusterer,
    FullCalendarModule,
    // ✅ Export Custom Components
    FooterComponent,
    CalendarModule,
    DashboardHeaderComponent,
    AiUsageComponent,
    NoPermissionComponent,
    ReloginComponent,
    DeleteComponent,
    ClientsubnavComponent,
    SystemMessageComponent,
    PayrollBillingComponent,
    SkeletonModule,
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ChipsModule,
    TooltipModule,
    TextareaModule,
    ReactiveFormsModule,
    DialogModule,
    AngularEditorModule,
    DragDropModule,
    NgxSpinnerModule,
    NgxPaginationModule,
    NgxPrintModule,
    TranslateModule,
    // ✅ PrimeNG 15 Standalone Components (import them directly)
    AccordionModule,
    ToggleSwitchModule,
    CheckboxModule,
    ProgressBarModule,
    Select,
    PasswordModule,
    ToastModule,
    MenuModule,
    TableModule,
    ButtonModule,
    TieredMenuModule,
    BreadcrumbModule,
    RadioButton,
    MessagesModule,
    MessageModule,
    DatePicker,
    CalendarModule,
    InputTextModule,
    InputMaskModule,
    Fluid,
    TabsModule,
    EditorModule,
    OverlayPanelModule,
    FileUploadModule,
    PaginatorModule,
    SplitButtonModule,
    AutoCompleteModule,
    MultiSelectModule,
    ChartModule,
    GoogleMap,
    MapMarker,
    MapAdvancedMarker,
    MapInfoWindow,
    MapMarkerClusterer,
    FullCalendarModule,
    ChipsModule,
    DrawerModule,
    TagModule,
    SkeletonModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterseptorsService,
      multi: true,
    },
    SharedService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class SharedModule {}
